import { useState, useEffect } from "react";
import { useAppSelector } from "~/hooks/redux";
import { useAuth } from "~/context/auth";
import { eSignConsent, updatePaperCopyRequired, getOrderDocSigners } from "~/helpers/backendHelper";
import { showError } from "~/helpers/utilHelper";
import OrderSigner from "~/model/orderSigner";
import UserRole from "~/model/userRole";

/**
 * Custom hook for managing ESIGN consent flow
 * @param {Object} options - Configuration options
 * @param {number|null} options.docId - Document ID for refreshing signers (optional)
 * @param {Function} options.onConsentSuccess - Callback when consent is successfully given (optional)
 * @param {Function} options.onConsentError - Callback when consent fails (optional)
 * @param {Function} options.refreshOrderSigner - Callback to refresh orderSigner from external source (optional, for Contracts.jsx)
 * @returns {Object} - State and handlers for ESIGN consent flow
 */
const useEsignConsent = (options: any = {}) => {

  const { docId = null, onConsentSuccess = null, onConsentError = null, refreshOrderSigner = null } = options;

  const { user: authUser } = useAuth();
  const orderSigers = useAppSelector(state => state.OrderDoc.Single.orderDoc?.signers);

  // Agreement and PaperCopyRequest flow state
  const [isAgreementVisible, setIsAgreementVisible] = useState(false);
  const [isAgreementConfirmed, setIsAgreementConfirmed] = useState(false);
  const [isPaperCopyRequestClosed, setIsPaperCopyRequestClosed] = useState(false);
  const [isDealerAgreementVisible, setIsDealerAgreementVisible] = useState(false);
  const [orderSigner, setOrderSigner] = useState<OrderSigner | null>(null);

  // Calculate orderSigner address
  const orderSignerAddress = [
    orderSigner?.address || authUser?.address,
    orderSigner?.address2 || authUser?.address2,
    orderSigner?.city || authUser?.city,
    orderSigner?.zip || authUser?.zip,
    orderSigner?.state || authUser?.state,
  ].filter(Boolean).join(', ') || '';

  // Update orderSigner from Redux when available
  useEffect(() => {
    if (Array.isArray(orderSigers) && orderSigers.length > 0) {
      const currentUserSigner = orderSigers.find(signer => signer.id === authUser?.id) || orderSigers[0];
      setOrderSigner(currentUserSigner);
    }
  }, [orderSigers, authUser?.id]);

  // Check if the signer is a dealer (use authUser.type as fallback if orderSigers is not available)
  const isDealer = authUser?.type === OrderSigner.TYPE_DEALER || orderSigers?.[1]?.type === OrderSigner.TYPE_DEALER || orderSigers?.[2]?.type === OrderSigner.TYPE_DEALER;

  // Check if dealer has no e-sign consent (use orderSigner if available, otherwise check orderSigers)
  // If orderSigers is undefined, check orderSigner
  const hasNoESignConsent = (isDealer && orderSigner?.eSignConsentTs === null) ||
    (orderSigers ? (orderSigers[1]?.eSignConsentTs === null || orderSigers[2]?.eSignConsentTs === null) : (isDealer && orderSigner?.eSignConsentTs === null));

  // Check which signers need consent
  // If orderSigers is undefined, fall back to checking orderSigner
  const signer1NeedsConsent = orderSigers
    ? (orderSigers[0]?.eSignConsentTs === null && authUser?.id === orderSigers[0]?.id && UserRole.isCustomerSigner(authUser?.userRoleId!))
    : (orderSigner?.eSignConsentTs === null && orderSigner?.id === authUser?.id && UserRole.isCustomerSigner(authUser?.userRoleId!));

  const signer2NeedsConsent = orderSigers
    ? (orderSigers[1]?.eSignConsentTs === null && authUser?.id === orderSigers[1]?.id && UserRole.isCustomerSigner(authUser?.userRoleId!))
    : false;

  const dealerNeedsConsent = isDealer && hasNoESignConsent;

  // For signers 1 and 2: show Agreement and PaperCopyRequest flow
  // Also check orderSigner directly if orderSigers is undefined (for Contracts.jsx)
  // This handles the case when orderSigner is fetched from the first contract
  const showAgreementFlow = signer1NeedsConsent || signer2NeedsConsent ||
    (!orderSigers && orderSigner?.eSignConsentTs === null && UserRole.isCustomerSigner(authUser?.userRoleId!));

  // For dealer: show modal over content (only if not closed and needs consent)
  const displayAgreeModal = dealerNeedsConsent;

  // Check if user needs consent (for customers)
  const userNeedsConsent = showAgreementFlow || (orderSigner?.eSignConsentTs === null && UserRole.isCustomerSigner(authUser?.userRoleId!));

  // Refresh order signers (only if docId is provided)
  const refreshOrderSigners = async () => {
    if (!docId) return;
    try {
      await getOrderDocSigners(docId);
    } catch (err) {
      console.error("Failed to refresh signers:", err);
    }
  };

  // Handle e-sign consent
  const handleEsignConsent = async () => {
    try {
      const resp: any = await eSignConsent();
      if (!resp?.id) {
        showError("Invalid response");
        return;
      }

      await refreshOrderSigners();

      // update orderSigner with the new eSignConsentTs to prevent showing consent flow again
      // this is important for Contracts.jsx where orderSigers is undefined
      if (orderSigner) {
        setOrderSigner({
          ...orderSigner,
          eSignConsentTs: resp.eSignConsentTs || new Date().toISOString()
        });
      }

      // if refreshOrderSigner callback is provided (for Contracts.jsx), call it to refresh from first contract
      if (refreshOrderSigner) {
        await refreshOrderSigner();
      }

      if (onConsentSuccess) {
        onConsentSuccess();
      }
    } catch (err) {
      showError("Unable to agree to this e-sign document");
      if (onConsentError) {
        onConsentError(err);
      }
      throw err;
    }
  };

  // Handle paper copy update
  const handleUpdatePaperCopyRequired = async (value: boolean) => {
    try {
      await updatePaperCopyRequired({ isPaperCopyRequired: value });
    } catch (err) {
      showError("Unable to update paper copy required");
      throw err;
    }
  };

  // Handle paper copy confirm
  const handlePaperCopyConfirm = async (value: boolean) => {
    setIsAgreementVisible(false);
    setIsPaperCopyRequestClosed(true);

    try {
      // Update paper copy first, then handle e-sign consent
      await handleUpdatePaperCopyRequired(value);
      await handleEsignConsent();
    } catch (err) {
      setIsPaperCopyRequestClosed(false);
    }
  };

  // Handle paper copy cancel
  const handlePaperCopyCancel = async (value: boolean) => {
    setIsAgreementConfirmed(false);
    setIsPaperCopyRequestClosed(true);

    try {
      // Update paper copy first, then handle e-sign consent
      await handleUpdatePaperCopyRequired(value);
      await handleEsignConsent();
    } catch (err) {
      setIsPaperCopyRequestClosed(false);
      setIsAgreementConfirmed(true);
    }
  };

  // Reset all agreement flow states
  const resetAgreementFlow = () => {
    setIsAgreementVisible(false);
    setIsAgreementConfirmed(false);
    setIsPaperCopyRequestClosed(false);
    setIsDealerAgreementVisible(false);
  };

  return {
    // State
    isAgreementVisible,
    setIsAgreementVisible,
    isAgreementConfirmed,
    setIsAgreementConfirmed,
    isPaperCopyRequestClosed,
    setIsPaperCopyRequestClosed,
    isDealerAgreementVisible,
    setIsDealerAgreementVisible,
    orderSigner,
    setOrderSigner,
    orderSignerAddress,

    // Computed values
    isDealer,
    hasNoESignConsent,
    signer1NeedsConsent,
    signer2NeedsConsent,
    dealerNeedsConsent,
    showAgreementFlow,
    displayAgreeModal,
    userNeedsConsent,

    // Handlers
    handleEsignConsent,
    handleUpdatePaperCopyRequired,
    handlePaperCopyConfirm,
    handlePaperCopyCancel,
    resetAgreementFlow,
  };
};

export default useEsignConsent;

